"use client";

import { useAuth } from "@/context/AuthContext";
import { auth, db } from "@/lib/firebase";
import { signOut } from "firebase/auth";
import { Moon, Sun, LogOut, OctagonX, Play, Menu } from "lucide-react";
import { NotificationBell } from "@/components/layout/NotificationBell";
import Image from "next/image";
import { GlobalSearch } from "@/components/layout/GlobalSearch";
import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { AppLink as Link } from "@/components/AppLink";
import { usePathname } from "next/navigation";
import { useBrand } from "@/context/BrandContext";
import { OPERATIVA, HERRAMIENTAS, ADMIN } from "@/components/layout/Sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";

function getContextLabel(pathname: string): string | null {
  if (pathname === "/") return "Inicio";
  if (pathname.startsWith("/emision") && !pathname.startsWith("/emision/control-recibos")) {
    if (pathname === "/emision") return "Emisión Clásicos";
    if (pathname.startsWith("/emision/")) return "Detalle solicitud";
    return "Emisión";
  }
  if (pathname.startsWith("/pre-clasicos")) return "Pre-Clásicos";
  if (pathname.startsWith("/calendario")) return "Calendario";
  return null;
}

export function Header() {
  const { usuarioRPA } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const pathname = usePathname();
  const { isSegurclassic } = useBrand();
  const [pausaGlobal, setPausaGlobal] = useState(false);
  const [pausaLoading, setPausaLoading] = useState(false);
  const isAdmin = usuarioRPA?.rol === "admin";
  const contextLabel = getContextLabel(pathname);

  useEffect(() => {
    if (!isAdmin) return;
    const loadPausa = async () => {
      try {
        const snap = await getDoc(doc(db, "sistema", "config"));
        if (snap.exists()) {
          setPausaGlobal(snap.data().pausaGlobal || false);
        }
      } catch {
        // Ignorar error al cargar el estado de pausa
      }
    };
    loadPausa();
  }, [isAdmin]);

  const handlePanicToggle = async () => {
    try {
      setPausaLoading(true);
      const newState = !pausaGlobal;
      const configRef = doc(db, "sistema", "config");
      const snap = await getDoc(configRef);
      if (snap.exists()) {
        await updateDoc(configRef, { pausaGlobal: newState });
      } else {
        await setDoc(configRef, { pausaGlobal: newState });
      }
      setPausaGlobal(newState);
    } catch (err) {
      console.error("Error toggling pausa:", err);
    } finally {
      setPausaLoading(false);
    }
  };

  const handleSignOut = () => {
    signOut(auth);
  };

  return (
    <header
      className={`h-16 flex items-center justify-between px-6 shrink-0 header-brand-transition shadow-sm z-50 bg-gradient-to-r from-slate-900 via-slate-900 to-slate-950 text-slate-300 border-b ${
        isSegurclassic ? "border-segurclassic-gold/40" : "border-white/[0.06]"
      }`}
    >
      <div className="flex items-center gap-4 md:gap-6 min-w-0">
        <Link href="/" className="flex items-center gap-3 text-white shrink-0">
          <div className="relative w-9 h-9 rounded-lg bg-white shadow-md flex items-center justify-center">
            <Image src="/logo.ico" alt="MontMar Logo" fill sizes="36px" className="object-contain p-1.5" />
          </div>
          <span className="font-heading font-bold text-xl tracking-tight hidden sm:inline-block">
            MontMar <span className="text-emerald-400 font-normal">RPA</span>
          </span>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger
            className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-300 hover:text-white hover:bg-white/10"
            aria-label="Abrir navegación"
          >
            <Menu className="h-5 w-5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start" sideOffset={8}>
            <DropdownMenuLabel>Operativa</DropdownMenuLabel>
            {OPERATIVA.map(({ href, label }) => (
              <DropdownMenuItem key={href}>
                <Link href={href} className="w-full">{label}</Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Herramientas</DropdownMenuLabel>
            {HERRAMIENTAS.map(({ href, label }) => (
              <DropdownMenuItem key={href}>
                <Link href={href} className="w-full">{label}</Link>
              </DropdownMenuItem>
            ))}
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Admin</DropdownMenuLabel>
                {ADMIN.map(({ href, label }) => (
                  <DropdownMenuItem key={href}>
                    <Link href={href} className="w-full">{label}</Link>
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {isSegurclassic && contextLabel && (
          <div className="hidden lg:flex items-center gap-2 pl-4 border-l border-white/10 animate-fade-in">
            <div className="relative w-7 h-5 rounded bg-segurclassic-lightgreen/80 border border-segurclassic-gold/30">
              <Image src="/segurclassic-logo.png" alt="" fill sizes="28px" className="object-contain p-0.5" />
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-widest text-segurclassic-gold">
              SegurClassic · {contextLabel}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-1 justify-center max-w-xl mx-4 lg:mx-8 hidden lg:flex text-slate-900 dark:text-slate-100">
        <div className="w-full relative">
          <GlobalSearch />
        </div>
      </div>

      <div className="flex items-center gap-1.5 md:gap-3 shrink-0">
        <div className="lg:hidden text-slate-900 dark:text-slate-100">
          <GlobalSearch />
        </div>

        {isAdmin && (
          <Button
            variant={pausaGlobal ? "destructive" : "outline"}
            size="sm"
            onClick={handlePanicToggle}
            disabled={pausaLoading}
            className={`h-9 px-3 text-xs font-semibold transition-all border-white/10 ${
              pausaGlobal
                ? "bg-red-600 hover:bg-red-700 text-white animate-pulse shadow-lg shadow-red-500/30 border-0"
                : "bg-transparent text-slate-300 hover:text-red-400 hover:border-red-500/50 hover:bg-red-500/10"
            }`}
            title={pausaGlobal ? "Bots PAUSADOS — Click para reanudar" : "Pausar todos los bots"}
          >
            {pausaGlobal ? (
              <><Play className="h-4 w-4 mr-1.5" /> Reanudar</>
            ) : (
              <><OctagonX className="h-4 w-4 mr-1.5" /> Pausar Bots</>
            )}
          </Button>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
          className="text-slate-300 hover:text-white hover:bg-white/10 rounded-full h-9 w-9"
          aria-label={resolvedTheme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
        >
          {resolvedTheme === "dark" ? (
            <Sun className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Moon className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>

        <div className="text-slate-900 dark:text-slate-100">
          <NotificationBell />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded-full ml-1"
            aria-label={`Menú de usuario: ${usuarioRPA?.nombre ?? "Usuario"}`}
          >
            <div className="flex items-center gap-2 pl-3 border-l border-white/10">
              <div className="w-9 h-9 rounded-full bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-sm shadow-sm transition-transform hover:scale-105 overflow-hidden" aria-hidden="true">
                {usuarioRPA?.avatarUrl ? (
                  <img src={usuarioRPA.avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  usuarioRPA?.nombre?.charAt(0) || "U"
                )}
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" sideOffset={8}>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="font-normal p-3">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{usuarioRPA?.nombre} {usuarioRPA?.apellido}</p>
                  <p className="text-xs leading-none text-muted-foreground mt-1">{usuarioRPA?.email}</p>
                  <div className="mt-2 text-[10px] uppercase font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30 py-1 px-2 rounded w-fit">
                    {usuarioRPA?.rol === "admin" ? "Administrador" : "Usuario"}
                  </div>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700 dark:focus:bg-red-950/50 py-2.5" onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              <span className="font-medium">Cerrar sesión</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
